
import { X, Filter, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DateFilter } from "./DateFilter";
import { RangeFilters } from "./RangeFilters";
import { DistanceFilter } from "./DistanceFilter";
import { TagFilters } from "./TagFilters";
import { DateRange } from "@/hooks/useSearchFilters";
import { useEffect, useState, useRef } from "react";
import { Input } from "@/components/ui/input";
import { useLocationServices } from "@/hooks/useLocationServices";

interface FilterOverlayProps {
  onClose: () => void;
  onReset: () => void;
  onSearch: () => void;
  dateRange: DateRange;
  setDateRange: (range: DateRange) => void;
  handleDatePreset: (preset: 'today' | 'this-week' | 'next-week') => void;
  availablePortions: number[];
  setAvailablePortions: (value: number[]) => void;
  deliveryOnly: boolean;
  setDeliveryOnly: (value: boolean) => void;
  distance: number;
  setDistance: (value: number[]) => void;
  rating: number[];
  setRating: (value: number[]) => void;
  recentSearches: string[];
  cuisineTypes: string[];
  dietaryTags: string[];
  allergens: string[];
  selectedFilters: string[];
  onToggleFilter: (filter: string) => void;
  searchQuery: string;
  onSearchQueryChange: (query: string) => void;
}

export const FilterOverlay = ({
  onClose,
  onReset,
  onSearch,
  dateRange,
  setDateRange,
  handleDatePreset,
  availablePortions,
  setAvailablePortions,
  deliveryOnly,
  setDeliveryOnly,
  distance,
  setDistance,
  rating,
  setRating,
  recentSearches,
  cuisineTypes,
  dietaryTags,
  allergens,
  selectedFilters,
  onToggleFilter,
  searchQuery,
  onSearchQueryChange,
}: FilterOverlayProps) => {
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const { trackInteraction } = useLocationServices();

  useEffect(() => {
    window.dispatchEvent(new CustomEvent('filterOpen'));
    
    // Track user opening the filter as an interaction
    trackInteraction('filter_overlay_open');
    
    return () => {
      window.dispatchEvent(new CustomEvent('filterClose'));
    };
  }, [trackInteraction]);

  useEffect(() => {
    const handleFilterRemoved = (event: CustomEvent) => {
      const removedFilter = event.detail;
      onToggleFilter(removedFilter);
    };

    window.addEventListener('filterRemoved', handleFilterRemoved as EventListener);

    return () => {
      window.removeEventListener('filterRemoved', handleFilterRemoved as EventListener);
    };
  }, [onToggleFilter]);

  const handleTouchStart = (e: React.TouchEvent) => {
    if (contentRef.current && contentRef.current.scrollTop === 0) {
      setTouchStart(e.targetTouches[0].clientY);
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (touchStart) {
      setTouchEnd(e.targetTouches[0].clientY);
    }
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isSwipeDown = distance < -100;
    if (isSwipeDown && contentRef.current && contentRef.current.scrollTop === 0) {
      onClose();
    }
    setTouchStart(null);
    setTouchEnd(null);
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      onSearch();
    }
  };

  const handleSearch = () => {
    // Track search as a user interaction
    trackInteraction('filter_search');
    onSearch();
  };

  const handleReset = () => {
    // Track filter reset as a user interaction
    trackInteraction('filter_reset');
    onReset();
  };

  // Fixed function to properly handle distance setting
  const handleDistanceChange = (values: number[]) => {
    setDistance(values);
  };

  return (
    <div className="fixed inset-0 isolate z-[9999]">
      <div className="absolute inset-0 bg-background" />
      <div className="relative z-10 flex flex-col h-[100dvh]">
        <div className="flex-none px-4 pt-4 pb-4 border-b bg-background">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-sm font-medium">Filters</h3>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleReset}
                className="text-black"
              >
                <Filter className="h-4 w-4 mr-2" />
                Reset filters
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={onClose}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>

          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <Input
              type="search"
              placeholder="Search meals..."
              value={searchQuery}
              onChange={(e) => {
                e.preventDefault();
                onSearchQueryChange(e.target.value);
              }}
              onKeyPress={handleKeyPress}
              className="w-full pl-10 pr-4"
              autoComplete="off"
              autoCorrect="off"
              spellCheck="false"
            />
          </div>
        </div>

        <div 
          ref={contentRef}
          className="flex-1 overflow-y-auto bg-background"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <div className="px-4 py-6 space-y-6 pb-24">
            <DateFilter
              dateRange={dateRange}
              setDateRange={setDateRange}
              handleDatePreset={handleDatePreset}
            />

            <DistanceFilter
              distance={distance}
              setDistance={handleDistanceChange}
            />

            <TagFilters
              title="Allergens"
              tags={allergens}
              selectedFilters={selectedFilters}
              onToggleFilter={onToggleFilter}
            />

            <div className="hidden">
              <RangeFilters
                availablePortions={availablePortions}
                setAvailablePortions={setAvailablePortions}
                deliveryOnly={deliveryOnly}
                setDeliveryOnly={setDeliveryOnly}
                rating={rating}
                setRating={setRating}
              />

              {recentSearches.length > 0 && (
                <TagFilters
                  title="Recent Searches"
                  tags={recentSearches}
                  selectedFilters={selectedFilters}
                  onToggleFilter={onToggleFilter}
                />
              )}

              <TagFilters
                title="Cuisine Type"
                tags={cuisineTypes}
                selectedFilters={selectedFilters}
                onToggleFilter={onToggleFilter}
              />

              <TagFilters
                title="Dietary Tags"
                tags={dietaryTags}
                selectedFilters={selectedFilters}
                onToggleFilter={onToggleFilter}
              />
            </div>
          </div>
        </div>

        <div className="flex-none px-4 py-4 border-t bg-background">
          <Button 
            className="w-full"
            onClick={handleSearch}
          >
            Search
          </Button>
        </div>
      </div>
    </div>
  );
};
